import React, { useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { ReactComponent as ParcelBox } from "assets/images/icons/ParcelBox.svg";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import { ReactComponent as RateCardIcon } from "assets/images/icons/RateCard.svg";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import usePermissions from "hooks/usePermissions";
import { areAllProvidersRequired, getProviderSelectionKey, isProviderEntrySelected } from "helpers/requestFormProvider";

type ProviderParcelAccount = {
  account_name: string;
  account_number: string;
  id: number;
  label: string;
  value: any;
};

type ProviderOption = {
  available_freight_modes: string[];
  company_logo: string;
  company_name: string;
  id: number;
  is_parcel_company: boolean;
  parcel_accounts: Array<ProviderParcelAccount>;
  provider_reference?: string;
  rate_card_available: boolean;
};

type ProviderSelectorProps = {
  providerData: ProviderOption;
  state: RequestFormState;
  isNewShipment?: boolean;
};

function ProviderSelector(props: ProviderSelectorProps) {
  const { providerData, state, isNewShipment } = props;
  const [imageLoadError, setImageLoadError] = useState(false);
  const { setValue, getValues, control, clearErrors } = useFormContext();

  const { permissions } = usePermissions();
  const { default_providers_invitation } = JSON.parse(permissions || "{}").company_shipper_settings;

  const selectedProviders = getValues("invited_providers.providers") || [];
  const selectedParcelAccounts = useWatch({ name: "invited_providers.parcel_accounts", control }) || [];
  const isSelected = isProviderEntrySelected(providerData, selectedProviders);
  const providerSelectionKey = getProviderSelectionKey(providerData);
  const allRequired = areAllProvidersRequired(
    default_providers_invitation,
    state.providers?.minimum_invites_possible ?? 0,
    state.providers?.invited_providers?.length ?? 0,
  );

  const handleProviderSelect = () => {
    let updatedProviders = [...selectedProviders];

    if (isSelected) {
      if (allRequired) {
        return;
      }
      updatedProviders = updatedProviders.filter((entry) => !isProviderEntrySelected(providerData, [entry]));
    } else {
      updatedProviders.push(providerSelectionKey);
      if (updatedProviders.length >= state.providers?.minimum_invites_possible && !isNewShipment) {
        clearErrors("invited_providers");
      }
      if (updatedProviders.length >= 1 && isNewShipment) {
        clearErrors("invited_providers");
      }
    }
    setValue("invited_providers.providers", updatedProviders);
  };

  const handleParcelAccountSelect = (val: any) => {
    let parcelAccounts = selectedParcelAccounts.slice();
    const existingParcelAccount = parcelAccounts.find((item: any) =>
      isProviderEntrySelected(providerData, [item.provider_id]),
    );

    if (!val.value && existingParcelAccount) {
      if (allRequired) {
        return;
      }
      parcelAccounts = parcelAccounts.filter((item: any) => !isProviderEntrySelected(providerData, [item.provider_id]));
      if (isSelected) {
        handleProviderSelect();
      }
    } else if (val.value) {
      if (existingParcelAccount) {
        parcelAccounts = parcelAccounts.map((item: any) => {
          if (isProviderEntrySelected(providerData, [item.provider_id])) {
            return {
              ...item,
              parcel_account_id: val.id,
            };
          }
          return item;
        });
      } else {
        parcelAccounts.push({ parcel_account_id: val.id, provider_id: providerSelectionKey });
      }
      if (!isSelected) {
        handleProviderSelect();
      }
    }
    setValue("invited_providers.parcel_accounts", parcelAccounts);
  };

  const renderIconContainer = useCallback(
    () => (
      <div className="icon-container">
        {providerData?.company_logo && !imageLoadError ? (
          <img
            src={providerData.company_logo}
            alt="provider-logo"
            className="provider-logo"
            onError={() => setImageLoadError(true)}
          />
        ) : (
          <ParcelBox width={14} height={16} />
        )}
      </div>
    ),
    [providerData.company_logo, imageLoadError],
  );

  return !providerData?.parcel_accounts?.length ? (
    <button
      className={`provider-selector ${isSelected ? "selected" : ""} ${allRequired ? "cursor-not-allowed" : ""}`}
      type="button"
      onClick={handleProviderSelect}
      disabled={allRequired}
    >
      {renderIconContainer()}
      <p className="provider-title">{providerData.company_name}</p>
      {providerData.rate_card_available && (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="freight-mode">
              <span className="font-weight-normal">Rate card available</span>
            </Tooltip>
          }
        >
          <RateCardIcon className="ml-2" />
        </OverlayTrigger>
      )}
    </button>
  ) : (
    <div className="flex-row">
      <div className="dropdown-div">
        <Dropdown
          onChange={handleParcelAccountSelect}
          value={
            selectedParcelAccounts.find((item: any) => isProviderEntrySelected(providerData, [item.provider_id]))
              ?.parcel_account_id
          }
          options={providerData.parcel_accounts}
          activeMode={isSelected}
          canDeselect={!allRequired}
          customLabel={({ label: selected }: any) => (
            <div className="d-flex align-items-center h-100 custom-label">
              {renderIconContainer()}
              <p className="provider-title">{!selected ? providerData.company_name : selected}</p>
              {providerData.rate_card_available && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="freight-mode">
                      <span className="font-weight-normal">Rate card available</span>
                    </Tooltip>
                  }
                >
                  <RateCardIcon className="ml-2" />
                </OverlayTrigger>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
}

ProviderSelector.defaultProps = {
  isNewShipment: false,
};

export default ProviderSelector;
